import { MUSIC_ENABLED_KEY, SOUNDS_ENABLED_KEY } from '@root/constants';
import { cdnAsset, localFromCdn, onPageVisibility } from '@shared/lib';

// Served from public/game/audio, not bundled. The design handover had no
// audio in it, so there is no track yet and music stays silent.
const BACKGROUND_MUSIC_PATH = '';

const MUSIC_VOLUME = 0.5;

// Menu toggles default to on when the key was never set. Match that here.
const readEnabled = (key: string) => {
  const saved = localStorage.getItem(key);
  return saved === null || saved === 'true';
};

// Fall back to the local copy if the CDN one fails.
const sourceWithFallback = (audio: HTMLAudioElement, path: string) => {
  audio.addEventListener(
    'error',
    () => {
      const local = localFromCdn(audio.src);
      if (local !== audio.src) audio.src = local;
    },
    { once: true }
  );
  audio.src = cdnAsset(path);
};

// First one to fire retries music that autoplay blocked.
const UNLOCK_EVENTS = ['pointerdown', 'touchstart', 'keydown'] as const;

class GameAudio {
  private music: HTMLAudioElement | null = null;
  private musicEnabled = readEnabled(MUSIC_ENABLED_KEY);
  private soundEnabled = readEnabled(SOUNDS_ENABLED_KEY);
  private unlockListenersAttached = false;

  constructor() {
    onPageVisibility({
      hidden: () => this.music?.pause(),
      visible: () => {
        if (this.musicEnabled) this.music?.play().catch(() => {});
      },
    });
  }

  private getMusic(): HTMLAudioElement | null {
    if (!BACKGROUND_MUSIC_PATH) return null;
    if (!this.music) {
      const audio = new Audio();
      audio.loop = true;
      audio.volume = MUSIC_VOLUME;
      sourceWithFallback(audio, BACKGROUND_MUSIC_PATH);
      this.music = audio;
    }
    return this.music;
  }

  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  setMusicEnabled(enabled: boolean) {
    this.musicEnabled = enabled;
    if (!enabled) {
      this.music?.pause();
      return;
    }
    this.getMusic()
      ?.play()
      .catch(() => {});
  }

  setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
  }

  startMusic() {
    if (!this.musicEnabled) return;
    const music = this.getMusic();
    if (!music) return;
    music.currentTime = 0;
    music.play().catch(() => this.attachAutoplayUnlock());
  }

  stopMusic() {
    this.music?.pause();
  }

  // The scene can be ready before the page has had a click, and autoplay
  // blocks that. Retry on the next click.
  private attachAutoplayUnlock() {
    if (this.unlockListenersAttached) return;
    this.unlockListenersAttached = true;

    const retry = () => {
      UNLOCK_EVENTS.forEach((type) =>
        document.removeEventListener(type, retry)
      );
      this.unlockListenersAttached = false;
      if (this.musicEnabled) {
        this.getMusic()
          ?.play()
          .catch(() => {});
      }
    };

    UNLOCK_EVENTS.forEach((type) =>
      document.addEventListener(type, retry, { once: true, passive: true })
    );
  }
}

export const gameAudio = new GameAudio();
